import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader, ArrowUp, ArrowDown, Settings, Save, XCircle, GripVertical } from 'lucide-react';
import { Promotion } from '../../../shared/types';
import { bannerService, ProdutoBannerOtimizado } from '../../banners/services/bannerService';

interface ScreenProductsModalProps {
    screenId: number;
    screenName: string;
    promotions: Promotion[];
    onClose: () => void;
}

export const ScreenProductsModal: React.FC<ScreenProductsModalProps> = ({
    screenId,
    screenName,
    promotions,
    onClose
}) => {
    const [produtosOtimizados, setProdutosOtimizados] = useState<ProdutoBannerOtimizado[]>([]);
    const [loadingProdutos, setLoadingProdutos] = useState(true);
    const [customizeMode, setCustomizeMode] = useState(false);
    const [orderedProductIds, setOrderedProductIds] = useState<number[]>([]);
    const [savingOrder, setSavingOrder] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [telaTipo, setTelaTipo] = useState<number | undefined>(undefined);
    const [initialOrder, setInitialOrder] = useState<number[] | null>(null);
    const [highlightIds, setHighlightIds] = useState<Set<number>>(new Set());
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
    const [draggingProductId, setDraggingProductId] = useState<number | null>(null);
    const [dragOverProductId, setDragOverProductId] = useState<number | null>(null);
    const [dragOverPosition, setDragOverPosition] = useState<'above' | 'below' | null>(null);

    // Buscar produtos otimizados apenas quando o modal abrir (método otimizado)
    useEffect(() => {
        const carregarProdutos = async () => {
            setLoadingProdutos(true);
            try {
                // Evita busca duplicada: se já temos promotions (fonte principal da listagem),
                // só buscamos dados otimizados caso haja necessidade (ex.: para código de barras)
                if (Array.isArray(promotions) && promotions.length > 0) {
                    // Busca leve para complementar metadados, mas não altera a lista base
                    try {
                        const produtos = await bannerService.buscarProdutosTelaOtimizadoComParametro(screenId);
                        setProdutosOtimizados(produtos || []);
                    } catch {
                        setProdutosOtimizados([]);
                    }
                } else {
                    // Sem promotions: usa dados otimizados como base
                    const produtos = await bannerService.buscarProdutosTelaOtimizadoComParametro(screenId);
                    setProdutosOtimizados(produtos || []);
                }
            } catch (error) {
                setProdutosOtimizados([]);
            } finally {
                setLoadingProdutos(false);
            }
        };

        carregarProdutos();
        // Nota: dependemos de screenId e promotions para decidir o caminho de carga
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenId, promotions?.length]);

    // Buscar a tela para obter a ordem atual (produtoIds) e tipo
    useEffect(() => {
        const carregarTela = async () => {
            try {
                const telas = await bannerService.listarTelasOtimizado(screenId);
                const tela = Array.isArray(telas) ? telas.find(t => t.id === screenId) : undefined;
                if (tela) {
                    setTelaTipo(tela.tipo);
                    if (Array.isArray(tela.produtoIds) && tela.produtoIds.length > 0) {
                        setOrderedProductIds(tela.produtoIds);
                    }
                }
            } catch {
                // Ignorar erros aqui; manterá o fallback para ordem alfabética
            }
        };
        carregarTela();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenId]);

    // Função para formatar números no padrão brasileiro
    const formatBrazilianNumber = (value: number | undefined, showCurrency: boolean = true): string => {
        const numValue = value ?? 0;
        const formattedNumber = numValue.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return showCurrency ? `R$ ${formattedNumber}` : formattedNumber;
    };

    // Combinar dados otimizados da API com dados do store (se disponível)
    const screenProducts = useMemo(() => {
        const products: Array<Promotion & { codigoBarras?: string }> = [];
        const seen = new Set<number>();
        
        produtosOtimizados.forEach(produtoOtimizado => {
            // Tentar buscar dados completos no store primeiro
            const productStore = promotions.find(p => p.id === produtoOtimizado.produtoId);
            
            // Evitar duplicatas por produtoId
            if (!produtoOtimizado?.produtoId || seen.has(produtoOtimizado.produtoId)) {
                return;
            }
            seen.add(produtoOtimizado.produtoId);
            
            if (productStore) {
                // Usar dados do store se disponível (mais completo)
                products.push({
                    ...productStore,
                    codigoBarras: produtoOtimizado.codigoBarras
                });
            } else {
                // Usar dados otimizados da API como fallback
                products.push({
                    id: produtoOtimizado.produtoId,
                    name: produtoOtimizado.nome || 'Produto sem nome',
                    price: produtoOtimizado.vlPreco || 0,
                    originalPrice: produtoOtimizado.vlPrecoNormal,
                    unit: produtoOtimizado.tipoUnidade || 'UN',
                    codigoBarras: produtoOtimizado.codigoBarras,
                    category: '',
                    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                    createdAt: new Date().toISOString()
                } as Promotion & { codigoBarras?: string });
            }
        });
        
        // Determinar ordem base SEMPRE pela API (produtosOtimizados) ou por orderedProductIds se já inicializado
        let order: number[] = [];
        if (orderedProductIds && orderedProductIds.length > 0) {
            order = orderedProductIds;
        } else if (Array.isArray(produtosOtimizados) && produtosOtimizados.length > 0) {
            // Ordem exata vinda da API
            const fromApi = produtosOtimizados
                .map(p => p.produtoId)
                .filter((id, idx, arr) => id != null && arr.indexOf(id) === idx) as number[];
            order = fromApi.length > 0 ? fromApi : products.map(p => p.id);
        } else {
            order = products.map(p => p.id);
        }

        const pos = new Map<number, number>();
        order.forEach((id, idx) => pos.set(id, idx));

        // Ordenar estritamente pela posição definida (NUNCA por ordem alfabética)
        const ordered = products.slice().sort((a, b) => {
            const ia = pos.has(a.id) ? (pos.get(a.id) as number) : Number.MAX_SAFE_INTEGER;
            const ib = pos.has(b.id) ? (pos.get(b.id) as number) : Number.MAX_SAFE_INTEGER;
            return ia - ib;
        });

        return ordered;
    }, [produtosOtimizados, promotions, customizeMode, orderedProductIds]);

    const ensureOrderedIdsInitialized = () => {
        if (!orderedProductIds || orderedProductIds.length === 0) {
            // Prioridade 1: ordem vinda da API (produtosOtimizados mantém a sequência de produtoIds da tela)
            if (Array.isArray(produtosOtimizados) && produtosOtimizados.length > 0) {
                const fromApi = produtosOtimizados
                    .map(p => p.produtoId)
                    .filter((id, idx, arr) => id != null && arr.indexOf(id) === idx);
                if (fromApi.length > 0) {
                    setOrderedProductIds(fromApi as number[]);
                    return;
                }
            }
            // Prioridade 2: se carregamos a tela com produtoIds (carregarTela), usa-os
            // (já estão em orderedProductIds se tiver sido setado anteriormente; aqui é fallback vazio)

            // Prioridade 3: fallback para IDs da lista visível (pode estar alfabética)
            const unique = screenProducts.map(p => p.id).filter((id, idx, arr) => arr.indexOf(id) === idx);
            setOrderedProductIds(unique);
        }
    };

    const handleToggleCustomize = () => {
        setSaveError(null);
        const willEnable = !customizeMode;
        if (willEnable) {
            ensureOrderedIdsInitialized();
            // Capturar ordem inicial para detectar mudanças e exibir feedback
            const base = (orderedProductIds && orderedProductIds.length > 0)
                ? orderedProductIds
                : screenProducts.map(p => p.id);
            setInitialOrder(base);
        }
        setCustomizeMode(willEnable);
        if (!willEnable) {
            setHighlightIds(new Set());
            setInitialOrder(null);
        }
    };

    const moveItem = (productId: number, direction: 'up' | 'down') => {
        setOrderedProductIds(prev => {
            const current = prev.length > 0 ? [...prev] : screenProducts.map(p => p.id);
            const index = current.indexOf(productId);
            if (index === -1) return current;
            const target = direction === 'up' ? index - 1 : index + 1;
            if (target < 0 || target >= current.length) return current;
            const list = [...current];
            const tmp = list[index];
            list[index] = list[target];
            list[target] = tmp;

            // Destacar visualmente os itens afetados
            const idsToHighlight = new Set<number>([list[index], list[target]]);
            setHighlightIds(idsToHighlight);
            window.setTimeout(() => {
                setHighlightIds(prevIds => {
                    // Remover destaque suavemente (mantém se mudou novamente)
                    if (!prevIds) return new Set();
                    const next = new Set(prevIds);
                    idsToHighlight.forEach(id => next.delete(id));
                    return next;
                });
            }, 700);
            return list;
        });
    };

    // Helpers e handlers para Drag & Drop
    const getCurrentOrderIds = (): number[] => {
        const base = (orderedProductIds && orderedProductIds.length > 0)
            ? orderedProductIds
            : screenProducts.map(p => p.id);
        // Garantir unicidade preservando a primeira ocorrência
        return base.filter((id, idx, arr) => arr.indexOf(id) === idx);
    };

    const handleDragStart = (productId: number, e: React.DragEvent<HTMLTableRowElement>) => {
        if (!customizeMode || savingOrder) return;
        setDraggingProductId(productId);
        setDragOverProductId(null);
        setDragOverPosition(null);
        try {
            e.dataTransfer.setData('text/plain', String(productId));
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.dropEffect = 'move';
        } catch {
            // alguns navegadores exigem setData
        }
    };

    const handleDragOver = (productId: number, e: React.DragEvent<HTMLTableRowElement>) => {
        if (!customizeMode || savingOrder) return;
        e.preventDefault();
        if (draggingProductId == null || draggingProductId === productId) {
            setDragOverProductId(null);
            setDragOverPosition(null);
            return;
        }
        const rect = (e.currentTarget as HTMLTableRowElement).getBoundingClientRect();
        const offsetY = e.clientY - rect.top;
        // Limiar levemente acima do meio para reduzir flicker e tornar mais previsível
        const position: 'above' | 'below' = offsetY < rect.height * 0.45 ? 'above' : 'below';
        setDragOverProductId(productId);
        setDragOverPosition(position);
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragLeave = (productId: number) => {
        if (dragOverProductId === productId) {
            setDragOverProductId(null);
            setDragOverPosition(null);
        }
    };

    const handleDrop = (productId: number, e: React.DragEvent<HTMLTableRowElement>) => {
        if (!customizeMode || savingOrder) return;
        e.preventDefault();
        const sourceId = draggingProductId;
        const targetId = productId;
        const position = dragOverPosition || 'below';
        setDragOverProductId(null);
        setDragOverPosition(null);
        if (sourceId == null || targetId == null || sourceId === targetId) return;

        const current = getCurrentOrderIds();
        const fromIndex = current.indexOf(sourceId);
        let toIndex = current.indexOf(targetId);
        if (fromIndex === -1 || toIndex === -1) return;

        // Ao soltar "acima", inserimos antes do alvo; "abaixo", inserimos após o alvo
        if (position === 'below') {
            toIndex = toIndex + 1;
        }
        // Remover origem antes para ajustar índice destino quando necessário
        const list = current.filter(id => id !== sourceId);
        // Ajustar índice caso a remoção tenha afetado a posição alvo
        if (fromIndex < toIndex) {
            toIndex = Math.max(0, toIndex - 1);
        }
        // Limitar dentro do intervalo
        if (toIndex < 0) toIndex = 0;
        if (toIndex > list.length) toIndex = list.length;
        list.splice(toIndex, 0, sourceId);

        setOrderedProductIds(list);
        // Realçar os itens envolvidos
        const idsToHighlight = new Set<number>([sourceId, targetId]);
        setHighlightIds(idsToHighlight);
        window.setTimeout(() => {
            setHighlightIds(prevIds => {
                if (!prevIds) return new Set();
                const next = new Set(prevIds);
                idsToHighlight.forEach(id => next.delete(id));
                return next;
            });
        }, 700);
    };

    const handleDragEnd = () => {
        setDraggingProductId(null);
        setDragOverProductId(null);
        setDragOverPosition(null);
    };

    const hasChanges = useMemo(() => {
        if (!customizeMode) return false;
        const current = (orderedProductIds && orderedProductIds.length > 0)
            ? orderedProductIds
            : screenProducts.map(p => p.id);
        const base = initialOrder || [];
        if (current.length !== base.length) return true;
        for (let i = 0; i < current.length; i++) {
            if (current[i] !== base[i]) return true;
        }
        return false;
    }, [customizeMode, orderedProductIds, screenProducts, initialOrder]);

    const handleSaveOrder = async () => {
        setSaveError(null);
        setSavingOrder(true);
        try {
            const idsBase = (orderedProductIds && orderedProductIds.length > 0)
                ? orderedProductIds
                : screenProducts.map(p => p.id);
            // Deduplicar preservando a primeira ocorrência
            const ids = idsBase.filter((id, idx, arr) => arr.indexOf(id) === idx);
            // LOG REQUERIDO
            console.log('ORDEM PARA SALVAR:', ids);
            // LOGS TEMPORÁRIOS
            console.log('[ScreenProductsModal] Salvar ordem - screenId:', screenId, {
                baseIds: idsBase,
                finalIds: ids
            });

            // Confirmar dados necessários da tela
            let tipo = telaTipo;
            let nome = screenName;
            try {
                const telas = await bannerService.listarTelasOtimizado(screenId);
                const tela = Array.isArray(telas) ? telas.find(t => t.id === screenId) : undefined;
                if (tela) {
                    tipo = tela.tipo ?? tipo;
                    nome = tela.nome || nome;
                }
            } catch {
                // usa os valores locais se falhar
            }

            // Buscar produtos atuais para desvincular todos antes de re-vincular na nova ordem
            const telasBefore = await bannerService.listarTelasOtimizado(screenId);
            const telaBefore = Array.isArray(telasBefore) ? telasBefore.find(t => t.id === screenId) : undefined;
            const idsAtuais = (telaBefore?.produtoIds && Array.isArray(telaBefore.produtoIds))
                ? (telaBefore.produtoIds as number[]).filter((id, idx, arr) => arr.indexOf(id) === idx)
                : [];
            console.log('[ScreenProductsModal] Snapshot atual antes de desvincular:', idsAtuais);

            // Se a ordem final for idêntica, não fazer chamadas desnecessárias
            const isSameOrder = ids.length === idsAtuais.length && ids.every((v, i) => v === idsAtuais[i]);
            if (!isSameOrder && idsAtuais.length > 0) {
                // 1) Desvincular todos os atuais
                const desvOk = await bannerService.desvincularProdutos(screenId, idsAtuais);
                console.log('[ScreenProductsModal] Desvincular resultado:', desvOk);
            }

            // 2) Vincular na nova ordem em UM ÚNICO PUT (igual ao fluxo de vínculo de produto):
            if (!isSameOrder) {
                const command: any = {
                    id: screenId,
                    nome,
                    tipo: (tipo ?? 0),
                    produtoIds: ids
                };
                console.log('[ScreenProductsModal] Vincular ordem nova (PUT único) payload:', command);
                const ok = await bannerService.atualizarTela(command);
                console.log('[ScreenProductsModal] atualizarTela resultado:', ok);
                if (!ok) throw new Error('Falha ao salvar a ordem dos produtos');
            }

            // Recarregar ordem da API para confirmar persistência e refletir imediatamente
            try {
                const telasAtualizadas = await bannerService.listarTelasOtimizado(screenId);
                const telaAtualizada = Array.isArray(telasAtualizadas) ? telasAtualizadas.find(t => t.id === screenId) : undefined;
                if (telaAtualizada && Array.isArray(telaAtualizada.produtoIds)) {
                    // Deduplicar também o retorno da API, caso venha com duplicadas
                    const apiIds = (telaAtualizada.produtoIds as number[]).filter((id, idx, arr) => arr.indexOf(id) === idx);
                    console.log('[ScreenProductsModal] API após salvar (produtoIds):', apiIds);
                    setOrderedProductIds(apiIds);
                    setInitialOrder(apiIds);
                } else {
                    console.log('[ScreenProductsModal] API não retornou produtoIds, mantendo ids locais:', ids);
                    setInitialOrder(ids);
                }
                // Recarregar produtos para manter consistência visual
                const produtos = await bannerService.buscarProdutosTelaOtimizadoComParametro(screenId);
                setProdutosOtimizados(produtos);
                console.log('[ScreenProductsModal] Produtos recarregados após salvar (qtd):', produtos.length);
            } catch {
                // Se falhar recarga, manter ordem local
                console.warn('[ScreenProductsModal] Falha ao recarregar tela/produtos depois de salvar. Mantendo ordem local.');
                setInitialOrder(ids);
            }

            // Manter no modo personalização e mostrar feedback de sucesso
            setSaveSuccess('Ordem salva com sucesso');
            window.setTimeout(() => setSaveSuccess(null), 2000);
        } catch (err: any) {
            console.error('[ScreenProductsModal] Erro ao salvar ordem:', err);
            setSaveError(err?.message || 'Erro ao salvar a ordem');
        } finally {
            setSavingOrder(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">
                                Produtos Vinculados
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Tela: {screenName}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleToggleCustomize}
                                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${customizeMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                                title="Personalizar ordem dos produtos"
                            >
                                <Settings size={16} />
                                <span className="text-sm font-medium">{customizeMode ? 'Personalizando...' : 'Personalizar ordem'}</span>
                            </button>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <X size={20} />
                        </button>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {customizeMode && (
                        <div className="mb-4 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-sm flex items-center justify-between">
                            <span>Use as setas para mover os produtos. As alterações não são salvas automaticamente.</span>
                            {hasChanges && (
                                <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-semibold">
                                    Ordem alterada
                                </span>
                            )}
                            {!hasChanges && saveSuccess && (
                                <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-semibold">
                                    {saveSuccess}
                                </span>
                            )}
                        </div>
                    )}
                    {loadingProdutos ? (
                        <div className="text-center py-12">
                            <div className="flex flex-col items-center justify-center">
                                <Loader size={32} className="animate-spin text-blue-500 mb-4" />
                                <p className="text-gray-600 text-lg font-medium">Carregando produtos...</p>
                                <p className="text-gray-400 text-sm mt-2">Buscando dados na API</p>
                            </div>
                        </div>
                    ) : screenProducts.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500 text-lg mb-2">Nenhum produto vinculado</p>
                            <p className="text-gray-400 text-sm">Esta tela ainda não possui produtos vinculados</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b">
                                        <tr>
                                            {customizeMode && (
                                                <th className="px-2 py-3 text-left text-sm font-medium text-gray-700 w-20">Ordem</th>
                                            )}
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-24">ID</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Nome</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-32">Preço</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-24">Unidade</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {screenProducts.map((product, idx) => (
                                            <tr
                                                key={product.id}
                                                draggable={customizeMode && !savingOrder}
                                                onDragStart={(e) => handleDragStart(product.id, e)}
                                                onDragOver={(e) => handleDragOver(product.id, e)}
                                                onDragLeave={() => handleDragLeave(product.id)}
                                                onDrop={(e) => handleDrop(product.id, e)}
                                                onDragEnd={handleDragEnd}
                                                className={`relative transition-colors duration-300 hover:bg-gray-50 ${customizeMode && highlightIds.has(product.id) ? 'bg-blue-50 border-l-4 border-blue-400' : ''} ${customizeMode && draggingProductId === product.id ? 'opacity-70 shadow-sm' : ''} ${customizeMode && dragOverProductId === product.id && dragOverPosition === 'above' ? 'bg-gradient-to-b from-blue-100 to-transparent shadow-[inset_0_1.5px_0_0_#2563eb]' : ''} ${customizeMode && dragOverProductId === product.id && dragOverPosition === 'below' ? 'bg-gradient-to-t from-blue-100 to-transparent shadow-[inset_0_-1.5px_0_0_#2563eb]' : ''} ${customizeMode ? 'cursor-grab' : ''}`}
                                            >
                                                {customizeMode && (
                                                    <td className="px-2 py-2 relative">
                                                        {dragOverProductId === product.id && dragOverPosition === 'above' && (
                                                            <div className="absolute -top-2 left-2 z-10 text-[10px] font-semibold px-2 py-0.5 rounded bg-blue-700 text-white shadow">
                                                                Soltar acima
                                                            </div>
                                                        )}
                                                        {dragOverProductId === product.id && dragOverPosition === 'below' && (
                                                            <div className="absolute -bottom-2 left-2 z-10 text-[10px] font-semibold px-2 py-0.5 rounded bg-blue-700 text-white shadow">
                                                                Soltar abaixo
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-2">
                                                            <div
                                                                className="flex flex-col gap-1"
                                                                draggable={false}
                                                                onMouseDown={(e) => { e.stopPropagation(); }}
                                                                onPointerDown={(e) => { e.stopPropagation(); }}
                                                                onTouchStart={(e) => { e.stopPropagation(); }}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className={`p-1 rounded ${idx === 0 || savingOrder ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-blue-600 hover:bg-blue-100'}`}
                                                                    draggable={false}
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    onPointerDown={(e) => e.stopPropagation()}
                                                                    onTouchStart={(e) => e.stopPropagation()}
                                                                    onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                                    onClick={() => moveItem(product.id, 'up')}
                                                                    disabled={idx === 0 || savingOrder}
                                                                    title="Mover para cima"
                                                                >
                                                                    <ArrowUp size={16} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`p-1 rounded ${idx === screenProducts.length - 1 || savingOrder ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-blue-600 hover:bg-blue-100'}`}
                                                                    draggable={false}
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    onPointerDown={(e) => e.stopPropagation()}
                                                                    onTouchStart={(e) => e.stopPropagation()}
                                                                    onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                                    onClick={() => moveItem(product.id, 'down')}
                                                                    disabled={idx === screenProducts.length - 1 || savingOrder}
                                                                    title="Mover para baixo"
                                                                >
                                                                    <ArrowDown size={16} />
                                                                </button>
                                                            </div>
                                                            <div
                                                                className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${draggingProductId === product.id ? 'cursor-grabbing bg-blue-50 border-blue-300' : 'cursor-grab hover:bg-blue-50 hover:border-blue-300 border-transparent'}`}
                                                                title="Arraste para reordenar"
                                                            >
                                                                <GripVertical size={14} className="text-gray-500" />
                                                                <span className="sr-only">Arraste para reordenar</span>
                                                            </div>
                                                            <span className={`px-2 py-1 rounded text-xs font-semibold ${highlightIds.has(product.id) ? 'bg-blue-200 text-blue-800 animate-pulse' : 'bg-blue-100 text-blue-700'}`}>
                                                                {idx + 1}
                                                            </span>
                                                        </div>
                                                    </td>
                                                )}
                                                {/* ID */}
                                                <td className="px-4 py-3">
                                                    <span className="text-sm text-gray-600 font-mono font-bold">
                                                        {product.id}
                                                    </span>
                                                </td>

                                                {/* Nome */}
                                                <td className="px-4 py-3">
                                                    <span className="text-sm text-gray-900 font-medium">{product.name}</span>
                                                </td>

                                                {/* Preço */}
                                                <td className="px-4 py-3">
                                                    <span className="text-sm text-gray-900 font-semibold">
                                                        {formatBrazilianNumber(product.price)}
                                                    </span>
                                                    {product.originalPrice && product.originalPrice > product.price && (
                                                        <div className="text-xs text-gray-500 line-through">
                                                            {formatBrazilianNumber(product.originalPrice)}
                                                        </div>
                                                    )}
                                                </td>

                                                {/* Unidade */}
                                                <td className="px-4 py-3">
                                                    <span className="text-sm text-gray-600">{product.unit}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t bg-gray-50">
                    <div className="flex flex-col gap-3">
                        <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-600">
                            Total: <span className="font-semibold">{screenProducts.length}</span> {screenProducts.length === 1 ? 'produto vinculado' : 'produtos vinculados'}
                        </p>
                            {saveError && (
                                <div className="flex items-center text-red-600 text-sm">
                                    <XCircle size={16} className="mr-1" /> {saveError}
                                </div>
                            )}
                        </div>
                        {customizeMode ? (
                            <div className="flex gap-3">
                                <button
                                    onClick={handleSaveOrder}
                                    disabled={savingOrder || !hasChanges}
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
                                >
                                    <Save size={16} />
                                    {savingOrder ? 'Salvando...' : hasChanges ? 'Salvar ordem' : 'Sem alterações'}
                                </button>
                                <button
                                    onClick={() => setCustomizeMode(false)}
                                    disabled={savingOrder}
                                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-60"
                                >
                                    Cancelar
                                </button>
                    </div>
                        ) : (
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        Fechar
                    </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
